import { useState } from "react";

import {
  useEmailConnections,
  useRemoveEmailConnection,
  useSetPrimaryEmailConnection,
} from "@/hooks/use-email-connections";
import {
  useRemoveResendConnection,
  useResendConnections,
  useSetPrimaryResendConnection,
} from "@/hooks/use-resend-connections";
import {
  useRemoveWhatsappDevice,
  useSetPrimaryWhatsappDevice,
  useWhatsappDevices,
  useWhatsappLogout,
  useWhatsappReconnect,
} from "@/hooks/use-whatsapp";
import { EmailConnectionDialog } from "@/pages/settings/email/email-connection-dialog";
import { EmailConnectionRow } from "@/pages/settings/email/email-connection-row";
import { ResendConnectionDialog } from "@/pages/settings/resend/resend-connection-dialog";
import { ResendConnectionRow } from "@/pages/settings/resend/resend-connection-row";
import { WhatsappDeviceRow } from "@/pages/settings/whatsapp/device-row";
import { WhatsappQrDialog } from "@/pages/settings/whatsapp/qr-dialog";

import type { EmailConnection, ResendConnection } from "@artisancode/api-types";

export function SenderConnections({
  qrDeviceId,
  onQrDeviceChange,
}: {
  qrDeviceId: string | null;
  onQrDeviceChange: (deviceId: string | null) => void;
}) {
  const { data: devices, isLoading: devicesLoading } = useWhatsappDevices();
  const logout = useWhatsappLogout();
  const reconnect = useWhatsappReconnect();
  const setPrimaryDevice = useSetPrimaryWhatsappDevice();
  const removeDevice = useRemoveWhatsappDevice();
  const activeDevice = devices?.find((d) => d.id === qrDeviceId);

  const { data: emailConnections, isLoading: emailLoading } =
    useEmailConnections();
  const setPrimaryEmail = useSetPrimaryEmailConnection();
  const removeEmail = useRemoveEmailConnection();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<EmailConnection | null>(
    null,
  );

  const { data: resendConnections, isLoading: resendLoading } =
    useResendConnections();
  const setPrimaryResend = useSetPrimaryResendConnection();
  const removeResend = useRemoveResendConnection();
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [editingResend, setEditingResend] = useState<ResendConnection | null>(
    null,
  );

  const isLoading = devicesLoading || emailLoading || resendLoading;
  const isEmpty =
    !isLoading &&
    (devices?.length ?? 0) === 0 &&
    (emailConnections?.length ?? 0) === 0 &&
    (resendConnections?.length ?? 0) === 0;

  return (
    <div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Memuat koneksi...</p>
      ) : isEmpty ? (
        <p className="text-sm text-muted-foreground">Belum ada koneksi.</p>
      ) : (
        <div className="grid gap-2">
          {devices?.map((device) => (
            <WhatsappDeviceRow
              key={device.id}
              device={device}
              logout={logout}
              reconnect={reconnect}
              setPrimary={setPrimaryDevice}
              remove={removeDevice}
              onConnect={onQrDeviceChange}
            />
          ))}
          {emailConnections?.map((connection) => (
            <EmailConnectionRow
              key={connection.id}
              connection={connection}
              setPrimary={setPrimaryEmail}
              remove={removeEmail}
              onEdit={(c) => {
                setEditingEmail(c);
                setEmailDialogOpen(true);
              }}
            />
          ))}
          {resendConnections?.map((connection) => (
            <ResendConnectionRow
              key={connection.id}
              connection={connection}
              setPrimary={setPrimaryResend}
              remove={removeResend}
              onEdit={(c) => {
                setEditingResend(c);
                setResendDialogOpen(true);
              }}
            />
          ))}
        </div>
      )}

      <WhatsappQrDialog
        deviceId={qrDeviceId}
        isLoggedIn={activeDevice?.isLoggedIn ?? false}
        onClose={() => onQrDeviceChange(null)}
      />
      <EmailConnectionDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        editing={editingEmail}
      />
      <ResendConnectionDialog
        open={resendDialogOpen}
        onOpenChange={setResendDialogOpen}
        editing={editingResend}
      />
    </div>
  );
}
